/**
 * Streaming CSV/TSV/JSONL importer using the File.stream() API.
 *
 * The previous FileReader-based loader in `shell.ts` loaded the entire file
 * into memory synchronously and then split it into lines. For a 1-million-row
 * CSV that meant a multi-hundred-MB string in memory before parsing started,
 * and a "1000 rows then stop" feel on any machine under memory pressure.
 *
 * This module:
 *  - Uses `File.stream().pipeThrough(new TextDecoderStream())` to read the file
 *    as UTF-8 chunks (default 64 KiB each) — bounded memory regardless of size.
 *  - Parses CSV with a proper state machine that handles quoted fields,
 *    doubled quotes, embedded delimiters and newlines, and chunk-boundary
 *    continuation (the existing `csv/parser.ts` already does this).
 *  - Streams batches of `setCell` commands into the kernel as soon as each
 *    batch fills, never holding more than one batch's worth of cells in JS
 *    memory outside the kernel.
 *  - Honors a 5,000,000 row ceiling and reports progress every ~25k rows.
 *  - Cancellable via AbortSignal — discards in-flight batches and stops the
 *    reader immediately.
 */

import { createCsvParser, parseCsvStream, type ParsedRow, type BadRow } from '../core/csv/parser.js';
import type { Command, ColumnarVectorPayload } from '../core/command/index.js';
import { MAX_COLS, type ScalarType } from '../core/types.js';
import type { KernelClient } from '../kernel/client.js';

export interface StreamImportProgress {
  rows: number;
  cells: number;
  percent?: number | undefined;
  throughputRowsPerSec?: number | undefined;
  elapsedMs?: number | undefined;
  estimatedRemainingMs?: number | undefined;
  badRowsCount?: number | undefined;
}

export interface StreamImportOptions {
  sheetId: string;
  /** Delimiter (auto-detected when omitted). */
  delimiter?: string | undefined;
  /** Whether the first row is a header (written to row 0 of the sheet). */
  hasHeader?: boolean | undefined;
  /** Maximum rows to import; defaults to 5,000,000. */
  rowLimit?: number | undefined;
  /** Batch size (rows per `dispatchBatch` call). */
  batchSize?: number | undefined;
  /** Estimated total rows for progress percent calculation. */
  estimatedTotalRows?: number | undefined;
  /** Periodic progress callback with detailed telemetry. */
  onProgress?: ((info: StreamImportProgress) => void) | undefined;
}

export interface StreamImportResult {
  rows: number;
  cells: number;
  cancelled: boolean;
  truncated: boolean;
  /** The header fields detected, if `hasHeader` was true. */
  headerFields: string[] | null;
  /** Malformed rows encountered during the streaming parse. */
  badRows: readonly BadRow[];
  badRowsCount: number;
  durationMs: number;
  throughputRowsPerSec: number;
}

const DEFAULT_ROW_LIMIT = 5_000_000;

/** Auto-detects the delimiter by evaluating top sample lines. */
export function detectDelimiter(input: string | string[]): string {
  const lines = (typeof input === 'string' ? input.split(/\r\n|\r|\n/) : input)
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
    .slice(0, 25);

  if (lines.length === 0) return ',';

  const candidates = [',', '\t', ';', '|'];
  let bestDelim = ',';
  let bestScore = -1;

  for (const delim of candidates) {
    let multiColLines = 0;
    let totalCols = 0;
    for (const line of lines) {
      const parsed = parseDelimitedLine(line, delim);
      if (parsed.length > 1) {
        multiColLines++;
        totalCols += parsed.length;
      }
    }
    // Prioritize delimiters that produce >1 columns across the most lines
    const score = multiColLines * 1000 + totalCols;
    if (multiColCountScore(multiColLines, score) > bestScore) {
      bestScore = multiColCountScore(multiColLines, score);
      bestDelim = delim;
    }
  }

  return bestDelim;
}

function multiColCountScore(multiColLines: number, score: number): number {
  return multiColLines > 0 ? score : -1;
}

/**
 * Streams a `File`, `Blob`, or `ReadableStream` into the kernel using the File Streams API.
 *
 * Implements:
 * - 0ms main thread freeze via cooperative micro-tick yielding.
 * - Strict bounded memory footprint (never buffers full CSV in memory).
 * - RFC 4180 multiline and quoted field continuation across stream cuts.
 * - Real-time progress and throughput calculation (rows/s).
 * - Instant cancellation via AbortSignal.
 * - Malformed row tracking and graceful row healing (padding/trimming).
 */
export async function streamImport(
  client: KernelClient,
  file: File | Blob | ReadableStream<Uint8Array>,
  opts: StreamImportOptions,
  signal?: AbortSignal,
): Promise<StreamImportResult> {
  const startTime = performance.now();
  const rowLimit = opts.rowLimit ?? DEFAULT_ROW_LIMIT;
  const sheetId = opts.sheetId;
  const hasHeader = opts.hasHeader ?? true;
  const estTotalRows = opts.estimatedTotalRows ?? 0;

  // Check if streaming is supported
  const isStreamable = file instanceof ReadableStream ||
    (typeof (file as Blob & { stream?: () => ReadableStream<Uint8Array> }).stream === 'function');

  if (!isStreamable) {
    return fallbackTextImport(client, file as File, opts, signal);
  }

  let totalRows = 0;
  let totalCells = 0;
  let truncated = false;
  let rowBuffer: string[][] = [];
  let headerFields: string[] | null = null;
  /** Row offset: 1 when header is present (header occupies row 0), 0 otherwise. */
  const dataRowOffset = hasHeader ? 1 : 0;

  // Kernel enforces MAX_COMMAND_CELLS = 1,048,576 per setRegion dispatch.
  // We keep each sub-batch bounded (max 65,536 cells per dispatch) for snappy IPC and low GC churn.
  const KERNEL_CELL_LIMIT = 65_536;

  const flushBatch = async (): Promise<void> => {
    if (rowBuffer.length === 0) return;
    if (signal?.aborted) {
      rowBuffer = [];
      return;
    }

    // Data rows start after the header row (row 0) when header is present
    const batchStartRow = (totalRows - rowBuffer.length) + dataRowOffset;
    const maxCols = Math.min(
      Math.max(...rowBuffer.map((r) => r.length), 1),
      MAX_COLS,
    );

    // Calculate how many rows fit per bounded sub-batch
    const safeRowsPerBatch = Math.max(1, Math.floor(KERNEL_CELL_LIMIT / maxCols));

    // Split the buffer into sub-batches that each stay within the cell limit
    for (let offset = 0; offset < rowBuffer.length; offset += safeRowsPerBatch) {
      if (signal?.aborted) break;
      const slice = rowBuffer.slice(offset, offset + safeRowsPerBatch);
      const startRow = batchStartRow + offset;

      // Transform row slice directly into typed columnar vectors
      const columns: ColumnarVectorPayload[] = [];
      for (let c = 0; c < maxCols; c++) {
        const name = headerFields?.[c] ?? `col_${c}`;
        const values: (number | boolean | string | null)[] = new Array(slice.length);
        let numCount = 0;
        let boolCount = 0;
        let strCount = 0;
        let emptyCount = 0;

        for (let r = 0; r < slice.length; r++) {
          const raw = slice[r]?.[c];
          if (raw === undefined || raw === '' || raw === null) {
            values[r] = null;
            emptyCount++;
            continue;
          }
          const trimmed = raw.trim();
          const upper = trimmed.toUpperCase();
          if (upper === 'TRUE' || upper === 'FALSE') {
            values[r] = upper === 'TRUE';
            boolCount++;
          } else {
            const num = Number(trimmed);
            if (!Number.isNaN(num) && Number.isFinite(num)) {
              values[r] = num;
              numCount++;
            } else {
              values[r] = raw;
              strCount++;
            }
          }
        }

        let colType: ScalarType = 'text';
        if (numCount > 0 && strCount === 0 && boolCount === 0) colType = 'number';
        else if (boolCount > 0 && strCount === 0 && numCount === 0) colType = 'bool';
        else if (emptyCount === slice.length) colType = 'empty';
        else if ((numCount > 0 || boolCount > 0) && strCount > 0) colType = 'mixed';

        columns.push({ name, type: colType, values });
      }

      const importCmd: Command = {
        k: 'importColumnarTable',
        sheet: sheetId,
        hasHeader: false,
        startRow,
        columns,
        noUndo: true,
      };

      const result = await client.dispatch(importCmd);
      if (!result.ok) {
        throw new Error(result.error.message || `CSV columnar import dispatch rejected at row ${startRow}`);
      }
      totalCells += slice.length * maxCols;
    }
    rowBuffer = [];
  };

  const parser = createCsvParser({
    ...(opts.delimiter ? { delimiter: opts.delimiter } : {}),
    hasHeader,
  });

  try {
    const rawStream: ReadableStream<Uint8Array> = file instanceof ReadableStream
      ? file
      : (file as Blob & { stream: () => ReadableStream<Uint8Array> }).stream();

    const reader = rawStream.getReader();
    let dynamicBatchRows = 4_096;
    let lastProgressTime = performance.now();
    let lastYieldTime = performance.now();

    while (!signal?.aborted) {
      const { value, done } = await reader.read();
      if (done) break;

      const parsedRows = parser.push(value);
      for (const row of parsedRows) {
        // Capture header row and write it to row 0 of the sheet
        if (hasHeader && row.row === 1) {
          headerFields = row.fields;
          const headerWidth = Math.min(row.fields.length, MAX_COLS);
          if (headerWidth > 0) {
            const hRes = await client.dispatch({
              k: 'setRegion',
              range: {
                sheet: sheetId,
                r0: 0,
                c0: 0,
                r1: 0,
                c1: headerWidth - 1,
              },
              inputs: [row.fields.slice(0, headerWidth)],
              noUndo: true,
            });
            if (!hRes.ok) {
              throw new Error(hRes.error.message || 'Header dispatch rejected by workbook engine');
            }
            totalCells += headerWidth;
          }
          continue;
        }

        if (totalRows >= rowLimit) {
          truncated = true;
          break;
        }

        rowBuffer.push(row.fields);
        totalRows += 1;

        // Adjust batch size dynamically based on actual row width
        if (rowBuffer.length === 1 && row.fields.length > 0) {
          const cols = Math.min(row.fields.length, MAX_COLS);
          dynamicBatchRows = Math.min(8_192, Math.max(1, Math.floor(KERNEL_CELL_LIMIT / cols)));
        }

        if (rowBuffer.length >= dynamicBatchRows) {
          await flushBatch();
          const now = performance.now();

          // Yield to event loop to guarantee 0ms UI freeze
          if (now - lastYieldTime > 20) {
            await new Promise((resolve) => setTimeout(resolve, 0));
            lastYieldTime = performance.now();
          }

          if (now - lastProgressTime > 100) {
            const elapsedSec = Math.max(0.001, (now - startTime) / 1000);
            const throughput = Math.round(totalRows / elapsedSec);
            const pct = estTotalRows > 0 ? Math.min(99, Math.round((totalRows / estTotalRows) * 100)) : undefined;
            const estRem = (estTotalRows > totalRows && throughput > 0)
              ? Math.round(((estTotalRows - totalRows) / throughput) * 1000)
              : undefined;

            lastProgressTime = now;
            opts.onProgress?.({
              rows: totalRows,
              cells: totalCells,
              percent: pct,
              throughputRowsPerSec: throughput,
              elapsedMs: Math.round(now - startTime),
              estimatedRemainingMs: estRem,
              badRowsCount: parser.badRows.length,
            });
          }
        }
      }

      if (truncated) break;
    }

    if (!truncated && !signal?.aborted) {
      const tail = parser.flush();
      for (const row of tail.rows) {
        if (hasHeader && row.row === 1 && headerFields === null) {
          headerFields = row.fields;
          const headerWidth = Math.min(row.fields.length, MAX_COLS);
          if (headerWidth > 0) {
            await client.dispatch({
              k: 'setRegion',
              range: {
                sheet: sheetId,
                r0: 0,
                c0: 0,
                r1: 0,
                c1: headerWidth - 1,
              },
              inputs: [row.fields.slice(0, headerWidth)],
              noUndo: true,
            });
            totalCells += headerWidth;
          }
          continue;
        }
        if (totalRows >= rowLimit) {
          truncated = true;
          break;
        }
        rowBuffer.push(row.fields);
        totalRows += 1;
      }
    }

    const durationMs = Math.max(1, Math.round(performance.now() - startTime));
    const throughputRowsPerSec = Math.round(totalRows / (durationMs / 1000));

    if (signal?.aborted) {
      await reader.cancel().catch(() => undefined);
      rowBuffer = [];
      return {
        rows: totalRows,
        cells: totalCells,
        cancelled: true,
        truncated,
        headerFields,
        badRows: parser.badRows,
        badRowsCount: parser.badRows.length,
        durationMs,
        throughputRowsPerSec,
      };
    }

    await flushBatch();

    opts.onProgress?.({
      rows: totalRows,
      cells: totalCells,
      percent: 100,
      throughputRowsPerSec,
      elapsedMs: durationMs,
      estimatedRemainingMs: 0,
      badRowsCount: parser.badRows.length,
    });

    return {
      rows: totalRows,
      cells: totalCells,
      cancelled: false,
      truncated,
      headerFields,
      badRows: parser.badRows,
      badRowsCount: parser.badRows.length,
      durationMs,
      throughputRowsPerSec,
    };
  } finally {
    rowBuffer = [];
  }
}

/**
 * A single-line, single-record delimited parser that understands quotes.
 * Used by the streaming path where the streaming CSV parser's batch state is
 * unnecessary (a single line is its own batch).
 */
/**
 * A single-line, single-record delimited parser that understands quotes.
 * Exported so the import preview modal can use the same parsing logic.
 */
export function parseDelimitedLine(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i] as string;
    if (inQuote) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuote = false;
        }
      } else {
        cur += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuote = true;
      continue;
    }
    if (ch === delimiter) {
      out.push(cur);
      cur = '';
      continue;
    }
    cur += ch;
  }
  out.push(cur);
  return out;
}

/** Fallback path for browsers without `File.stream()`. Reads whole file at once. */
async function fallbackTextImport(
  client: KernelClient,
  file: File,
  opts: StreamImportOptions,
  signal?: AbortSignal,
): Promise<StreamImportResult> {
  const text = await file.text();
  if (signal?.aborted) {
    return {
      rows: 0,
      cells: 0,
      cancelled: true,
      truncated: false,
      headerFields: null,
      badRows: [],
      badRowsCount: 0,
      durationMs: 0,
      throughputRowsPerSec: 0,
    };
  }
  const rowLimit = opts.rowLimit ?? DEFAULT_ROW_LIMIT;
  const hasHeader = opts.hasHeader ?? true;
  const sheetId = opts.sheetId;
  const delimiter = opts.delimiter ?? detectDelimiter(text.split(/\r\n|\r|\n/, 1)[0] ?? '');
  const commands: Command[] = [];
  let totalCells = 0;
  const lines = text.split(/\r\n|\r|\n/);
  let totalRows = 0;
  let headerFields: string[] | null = null;
  const dataRowOffset = hasHeader ? 1 : 0;

  // Write header row to row 0 if present
  if (hasHeader && lines.length > 0 && lines[0]) {
    headerFields = parseDelimitedLine(lines[0], delimiter);
    const headerWidth = Math.min(headerFields.length, MAX_COLS);
    for (let c = 0; c < headerWidth; c++) {
      const value = headerFields[c];
      if (value === undefined) continue;
      commands.push({
        k: 'setCell',
        addr: { sheet: sheetId, row: 0, col: c },
        input: value,
      });
      totalCells += 1;
    }
  }

  for (let i = hasHeader ? 1 : 0; i < lines.length; i++) {
    if (signal?.aborted) break;
    if (totalRows >= rowLimit) {
      break;
    }
    const line = lines[i];
    if (!line) continue;
    const fields = parseDelimitedLine(line, delimiter);
    const width = Math.min(fields.length, MAX_COLS);
    for (let c = 0; c < width; c++) {
      const value = fields[c];
      if (value === undefined) continue;
      commands.push({
        k: 'setCell',
        addr: { sheet: sheetId, row: totalRows + dataRowOffset, col: c },
        input: value,
      });
      totalCells += 1;
    }
    totalRows += 1;
  }
  if (commands.length > 0) {
    await client.dispatchBatch(commands, 'Import (fallback)');
  }
  return {
    rows: totalRows,
    cells: totalCells,
    cancelled: !!signal?.aborted,
    truncated: totalRows >= rowLimit,
    headerFields,
    badRows: [],
    badRowsCount: 0,
    durationMs: 0,
    throughputRowsPerSec: 0,
  };
}

// Re-export parseCsvStream so this module can be imported as a single facade
// by callers that only want the streaming API.
export { parseCsvStream };
export type { ParsedRow };
